/**
 * Unified JWT Token Validation Utilities
 *
 * Shared utilities for JWT token validation that work in both client and server environments
 */

export const JWT_CONSTANTS = {
  EXPIRY_THRESHOLD_MINUTES: 10,
  CLOCK_SKEW_BUFFER_SECONDS: 30,
};

/**
 * Decodes JWT payload without signature verification
 * @param {string} token - The JWT token
 * @returns {Object|null} - Decoded payload or null if invalid
 */
export function decodeJWTPayload(token) {
  if (!token || typeof token !== 'string') {
    return null;
  }

  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const payload = parts[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

/**
 * Gets the expiry timestamp from a JWT token
 * @param {string} token - The JWT token
 * @returns {number|null} - Expiry timestamp or null if invalid
 */
export function getTokenExpiry(token) {
  const payload = decodeJWTPayload(token);
  return payload?.exp || null;
}

/**
 * Checks if a JWT token is valid and not expired
 * @param {string} token - The JWT token to validate
 * @returns {boolean} - true if token is valid and not expired
 */
export function isJWTValid(token) {
  const payload = decodeJWTPayload(token);
  if (!payload?.exp) {
    return false;
  }

  const now = Math.floor(Date.now() / 1000);
  return payload.exp > now + JWT_CONSTANTS.CLOCK_SKEW_BUFFER_SECONDS;
}

/**
 * Checks if a JWT token is expired
 * @param {string} token - The JWT token
 * @returns {boolean} - true if token is expired
 */
export function isJWTExpired(token) {
  const payload = decodeJWTPayload(token);
  if (!payload?.exp) {
    return true; // Treat invalid tokens as expired
  }

  const now = Math.floor(Date.now() / 1000);
  return now >= payload.exp;
}

/**
 * Checks if a JWT token is expiring soon
 * @param {string} token - The JWT token
 * @param {number} minutes - Minutes threshold (default from constants)
 * @returns {boolean} - true if token expires within the threshold
 */
export function isJWTExpiringSoon(token, minutes = JWT_CONSTANTS.EXPIRY_THRESHOLD_MINUTES) {
  const payload = decodeJWTPayload(token);
  if (!payload?.exp) {
    return true; // Treat invalid tokens as expiring
  }

  const now = Math.floor(Date.now() / 1000);
  const threshold = minutes * 60;
  return payload.exp - now < threshold;
}

export function isUserJWTVerified(token) {
  const payload = decodeJWTPayload(token);

  return payload?.email_verified === true;
}
